// useSidebarBucketsState.js

import { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Hooks
import useBaseChannels from 'channels/hooks/useBaseChannels';

// Services
import { subscribeToBaseAppInstances } from 'services/appInstances/AppInstanceReadService';
import { ensureLazySystemDatabases } from 'services/databases/ensureLazySystemDatabases';
import { subscribeToBaseMembers } from 'services/bases/BaseMemberService';
// Utilities
import {
    buildBaseChannels,
    buildBaseConversations,
    buildBaseDatabases,
    buildBaseDashboards,
    buildBaseInstalledApps,
    composeBuckets,
} from '../utils/bucketTransforms';

import useRememberedOpenBucket from './useRememberedOpenBucket';
import useSidebarBucketsData from './useSidebarBucketsData';

import {
    getNodeTypesCacheForScope,
    makeCompositeScopeKey,
    makeBaseScopeKeyStrict,
} from 'services/nodetypes/NodeTypesCacheService';

import { ensureAiChannel } from 'spaces/services/aiConversationService';
import { getPersonName } from 'utilities/PersonIdentity';
import { buildBaseMemberProfile } from 'utilities/BaseMembers';

/**
 * useSidebarBucketsState
 *
 * Manages sidebar bucket data composition, remembered open state, and navigation handlers.
 * Create-modal openers are supplied by the parent (shared with AddModal launchers).
 *
 * @param {object} params - Hook parameters.
 * @param {function} params.onRequestClose - Called when a mobile sidebar action should close the drawer.
 * @param {function} params.onAddDashboard - Opens the new dashboard modal.
 * @param {function} params.onAddChannel - Opens the new channel modal.
 * @param {function} params.onCreateMessage - Opens the new message / conversation modal.
 * @param {function} params.onAddDatabase - Opens the new database modal.
 *
 * @returns {object} Grouped sidebar buckets state and handlers for the shell component.
 */
export default function useSidebarBucketsState({
    onRequestClose,
    onAddDashboard,
    onAddChannel,
    onCreateMessage,
    onAddDatabase,
}) {
    const language = useCurrentLanguage();
    const { getType } = useNodeTypes();
    const { user } = useUser();
    const { activeBase } = useBase();
    const navigate = useNavigate();

    const [nodeTypesTick, setNodeTypesTick] = useState(0);
    const [baseAppInstances, setBaseAppInstances] = useState([]);
    const [baseMembers, setBaseMembers] = useState([]);
    const { databases, dashboards } = useSidebarBucketsData({
        baseKey: activeBase?.key,
    });

    useEffect(() => {
        if (!activeBase?.key || !user?.key) return undefined;

        let cancelled = false;
        ensureLazySystemDatabases({
            baseKey: activeBase.key,
            userKey: user.key,
            databases,
            getType,
            language,
            forceTypeKeys: [],
        }).catch((error) => {
            if (!cancelled) {
                console.warn('[useSidebarBucketsState] ensureLazySystemDatabases failed:', error);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [activeBase?.key, databases, getType, language, user?.key]);

    const {
        channels,
        unreadByChannel,
    } = useBaseChannels({
        baseKey: activeBase?.key,
        user,
    });

    useEffect(() => {
        if (!activeBase?.key) {
            setBaseAppInstances([]);
            return undefined;
        }

        const unsubscribe = subscribeToBaseAppInstances(
            activeBase.key,
            (rows) => {
                setBaseAppInstances(Array.isArray(rows) ? rows : []);
            },
        );

        return unsubscribe;
    }, [activeBase?.key]);

    useEffect(() => {
        if (!activeBase?.key) {
            setBaseMembers([]);
            return undefined;
        }

        const unsubscribe = subscribeToBaseMembers(
            activeBase.key,
            (rows) => {
                setBaseMembers(Array.isArray(rows) ? rows : []);
            },
            () => {
                setBaseMembers([]);
            }
        );

        return () => unsubscribe?.();
    }, [activeBase?.key]);

    useEffect(() => {
        const scopeKey = makeCompositeScopeKey([
            makeBaseScopeKeyStrict(activeBase?.key),
            'ROOT',
        ]);

        const cache = getNodeTypesCacheForScope(scopeKey);
        const unsubscribe = cache.onChange(() => {
            setNodeTypesTick((prev) => prev + 1);
        });

        return () => unsubscribe?.();
    }, [activeBase?.key]);

    const ensureAiUserKey = user?.key || '';
    const ensureAiUserName = getPersonName(user, 'Member');

    useEffect(() => {
        if (!activeBase?.key || !ensureAiUserKey) return undefined;

        let cancelled = false;

        ensureAiChannel({
            baseKey: activeBase.key,
            userKey: ensureAiUserKey,
            userName: ensureAiUserName,
        }).catch((error) => {
            if (!cancelled) {
                console.warn('[useSidebarBucketsState] ensureAiChannel failed:', error);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [activeBase?.key, ensureAiUserKey, ensureAiUserName]);

    const activeBaseKey = typeof activeBase?.key === 'string'
        ? activeBase.key.trim()
        : '';

    const pinnedDashboardKey = useMemo(() => {
        if (!activeBaseKey) return '';

        const pinnedMap = user?.data_profile?.pinnedDashboardByBase;
        if (!pinnedMap || typeof pinnedMap !== 'object') return '';

        const value = pinnedMap[activeBaseKey];
        return typeof value === 'string' ? value.trim() : '';
    }, [activeBaseKey, user?.data_profile?.pinnedDashboardByBase]);

    const baseDatabases = useMemo(() => {
        return buildBaseDatabases({
            databases,
            getType,
            language,
            baseKey: activeBase?.key,
            nodeTypesTick,
        });
    }, [activeBase?.key, databases, getType, language, nodeTypesTick]);

    const baseInstalledApps = useMemo(() => {
        return buildBaseInstalledApps({
            appInstances: baseAppInstances,
            databases,
            getType,
            language,
            baseKey: activeBase?.key,
            nodeTypesTick,
        });
    }, [activeBase?.key, databases, getType, language, nodeTypesTick, baseAppInstances]);

    const baseDashboards = useMemo(() => {
        return buildBaseDashboards({
            dashboards,
            viewerUserKey: user?.key,
            pinnedDashboardKey,
        });
    }, [dashboards, pinnedDashboardKey, user?.key]);

    const baseChannels = useMemo(() => {
        return buildBaseChannels({ channels });
    }, [channels]);

    const membersByKey = useMemo(() => {
        const map = {};

        (Array.isArray(baseMembers) ? baseMembers : []).forEach((member) => {
            const profile = buildBaseMemberProfile(member);
            const key = String(profile?.userKey || profile?.key || '').trim();
            if (!key) return;
            map[key] = profile;
        });

        return map;
    }, [baseMembers]);

    const baseConversations = useMemo(() => {
        return buildBaseConversations({
            channels,
            currentUserKey: user?.key,
            membersByKey,
            unreadByChannel,
        });
    }, [channels, membersByKey, unreadByChannel, user?.key]);

    const buckets = useMemo(() => {
        return composeBuckets({
            baseDatabases,
            baseInstalledApps,
            baseDashboards,
            baseChannels,
            baseConversations,
            baseAppInstances,
        });
    }, [
        baseAppInstances,
        baseChannels,
        baseConversations,
        baseDashboards,
        baseDatabases,
        baseInstalledApps,
    ]);

    const { openBucketKey, setOpenBucketKey } = useRememberedOpenBucket({
        userKey: user?.key,
        baseKey: activeBase?.key,
        buckets,
    });

    const handleBucketToggle = useCallback((bucketKey) => {
        setOpenBucketKey((prev) => (prev === bucketKey ? null : bucketKey));
    }, [setOpenBucketKey]);

    const handleDashboardClick = useCallback((dashboardRow) => {
        const dashboardKey = dashboardRow?.key;
        if (!dashboardKey) return;

        onRequestClose?.();
        navigate(`/dashboard/${dashboardKey}`);
    }, [navigate, onRequestClose]);

    const handleChannelClick = useCallback((channelRow) => {
        const channelKey = channelRow?.key;
        if (!channelKey) return;

        navigate(`/channels/${channelKey}`);
    }, [navigate]);

    const handleAddDashboard = useCallback(() => {
        onAddDashboard?.();
    }, [onAddDashboard]);

    const handleAddDatabase = useCallback(() => {
        onAddDatabase?.();
    }, [onAddDatabase]);

    const handleConversationClick = useCallback((conversationRow) => {
        const channelKey = conversationRow?.key;
        if (!channelKey) return;

        onRequestClose?.();
        navigate(`/channels/${channelKey}`);
    }, [navigate, onRequestClose]);

    const handleCreateMessage = useCallback(() => {
        onCreateMessage?.();
    }, [onCreateMessage]);

    const handleAddChannel = useCallback(() => {
        onAddChannel?.();
    }, [onAddChannel]);

    return {
        bucketListState: {
            buckets,
            openBucketKey,
        },
        handlers: {
            handleBucketToggle,
            handleDashboardClick,
            handleChannelClick,
            handleConversationClick,
            handleCreateMessage,
            handleAddDashboard,
            handleAddChannel,
            handleAddDatabase,
        },
    };
}
